import type { Card, ccv3 } from '@proj-airi/ccc'

import { useLocalStorageManualReset } from '@proj-airi/stage-shared/composables'
import { watchDebounced } from '@vueuse/core'
import { defineStore, storeToRefs } from 'pinia'
import { computed } from 'vue'

import { DEFAULT_ARTISTRY_WIDGET_SPAWNING_PROMPT } from '../../constants/prompts/character-defaults'
import { fetchCharacterCard, getDefaultPreviewUrl, getDefaultVrmUrl } from '../../services/character-cards'
import { capturePosthogEvent } from '../analytics/posthog'
import { DisplayModelFormat, useDisplayModelsStore } from '../display-models'
import { useSettingsStageModel } from '../settings/stage-model'
import { useArtistryStore } from './artistry'
import { useConsciousnessStore } from './consciousness'
import { useSpeechStore } from './speech'

export interface AiriExtension {
  modules: {
    consciousness: {
      provider: string // Example: "openai"
      model: string // Example: "gpt-4o"
    }

    speech: {
      provider: string // Example: "elevenlabs"
      model: string // Example: "eleven_multilingual_v2"
      voice_id: string // Example: "alloy"

      pitch?: number
      rate?: number
      ssml?: boolean
      language?: string
    }

    vrm?: {
      source?: 'file' | 'url'
      file?: string // Example: "vrm/model.vrm"
      url?: string // Example: "https://example.com/vrm/model.vrm"
      previewUrl?: string
    }

    live2d?: {
      source?: 'file' | 'url'
      file?: string // Example: "live2d/model.json"
      url?: string // Example: "https://example.com/live2d/model.json"
      previewUrl?: string
    }

    // ID from display-models store (e.g. 'preset-live2d-1', 'display-model-<nanoid>')
    displayModelId?: string
    activeBackgroundId?: string

    hitInteractions?: Record<string, { expression?: string, motion?: string }>

    artistry?: {
      enabled?: boolean
      provider?: string
      model?: string
      promptPrefix?: string
      workflowId?: string
      widgetInstruction?: string
      spawnMode?: 'bg' | 'widget' | 'inline' | 'bg_widget'
      options?: Record<string, any>
      autonomousEnabled?: boolean
      autonomousThreshold?: number
      autonomousTarget?: 'user' | 'assistant'
    }
  }

  agents: {
    [key: string]: { // example: minecraft
      prompt: string
      enabled?: boolean
    }
  }

  /** ID of the remote character card this profile was created from, if any. */
  remoteCardId?: string
}

export interface AiriCard extends Card {
  /** Stable identity from card.json — used as chats.character_id */
  id: string
  extensions: {
    airi: AiriExtension
  } & Card['extensions']
}

export const useAiriCardStore = defineStore('airi-card', () => {
  const cards = useLocalStorageManualReset<Map<string, AiriCard>>('airi-cards', new Map())
  const activeCardId = useLocalStorageManualReset<string>('airi-card-active-id', 'default')

  const activeCard = computed(() => cards.value.get(activeCardId.value))

  const consciousnessStore = useConsciousnessStore()
  const speechStore = useSpeechStore()
  const artistryStore = useArtistryStore()
  const stageModelStore = useSettingsStageModel()
  const displayModelsStore = useDisplayModelsStore()

  const {
    activeProvider: activeConsciousnessProvider,
    activeModel: activeConsciousnessModel,
  } = storeToRefs(consciousnessStore)

  const {
    activeSpeechProvider,
    activeSpeechVoiceId,
    activeSpeechModel,
  } = storeToRefs(speechStore)

  const addCard = (card: AiriCard | Card | ccv3.CharacterCardV3) => {
    const cardId = (card as unknown as { id?: string }).id
    if (!cardId)
      throw new Error('addCard: card is missing required `id` field (card.json must have an `id`)')
    cards.value.set(cardId, newAiriCard(card))
    return cardId
  }

  const removeCard = (id: string) => {
    cards.value.delete(id)
    capturePosthogEvent('character_deleted', { character_id: id })
  }

  const updateCard = (id: string, updates: AiriCard | Card | ccv3.CharacterCardV3) => {
    const existingCard = cards.value.get(id)
    if (!existingCard)
      return false

    const updatedCard = {
      ...existingCard,
      ...updates,
    }

    cards.value.set(id, newAiriCard(updatedCard))
    return true
  }

  const getCard = (id: string) => {
    return cards.value.get(id)
  }

  function updateActiveCardDisplayModel(displayModelId: string | undefined, displayModelUrl?: string, displayModelFormat?: DisplayModelFormat) {
    const cardId = activeCardId.value
    const card = cards.value.get(cardId)
    if (!card)
      return false

    const extension = resolveAiriExtension(card)
    const modules: AiriExtension['modules'] = {
      ...extension.modules,
      displayModelId,
    }

    // Persist the model URL so it can be re-registered after page refresh
    // (in-memory displayModels are cleared on refresh, but card data survives in localStorage).
    if (displayModelUrl && displayModelFormat) {
      if (displayModelFormat === DisplayModelFormat.VRM) {
        modules.vrm = { ...extension.modules?.vrm, url: displayModelUrl }
        modules.live2d = extension.modules?.live2d
      }
      else if (displayModelFormat === DisplayModelFormat.Live2dZip) {
        modules.live2d = { ...extension.modules?.live2d, url: displayModelUrl }
        modules.vrm = extension.modules?.vrm
      }
    }

    cards.value.set(cardId, {
      ...card,
      extensions: {
        ...card.extensions,
        airi: {
          ...extension,
          modules,
        },
      },
    })

    return true
  }

  function resolveAiriExtension(card: Card | ccv3.CharacterCardV3): AiriExtension {
    // Get existing extension if available
    const existingExtension = ('data' in card
      ? card.data?.extensions?.airi
      : card.extensions?.airi) as AiriExtension

    // Create default modules config
    const defaultModules = {
      consciousness: {
        provider: activeConsciousnessProvider.value,
        model: activeConsciousnessModel.value,
      },
      speech: {
        provider: activeSpeechProvider.value,
        model: activeSpeechModel.value,
        voice_id: activeSpeechVoiceId.value,
      },
      displayModelId: stageModelStore.stageModelSelected,
      artistry: {
        enabled: false,
        provider: artistryStore.globalProvider,
        model: artistryStore.globalModel,
        promptPrefix: artistryStore.globalPromptPrefix,
        widgetInstruction: DEFAULT_ARTISTRY_WIDGET_SPAWNING_PROMPT,
        spawnMode: 'bg_widget' as const,
        options: artistryStore.globalProviderOptions,
        autonomousEnabled: false,
        autonomousThreshold: 70,
        autonomousTarget: 'assistant' as const,
      },
    } as const

    // Return default if no extension exists
    if (!existingExtension) {
      return {
        modules: defaultModules,
        agents: {},
      }
    }

    // Merge existing extension with defaults
    return {
      modules: {
        consciousness: {
          provider: existingExtension.modules?.consciousness?.provider ?? defaultModules.consciousness.provider,
          model: existingExtension.modules?.consciousness?.model ?? defaultModules.consciousness.model,
        },
        speech: {
          provider: existingExtension.modules?.speech?.provider ?? defaultModules.speech.provider,
          model: existingExtension.modules?.speech?.model ?? defaultModules.speech.model,
          voice_id: existingExtension.modules?.speech?.voice_id ?? defaultModules.speech.voice_id,
          pitch: existingExtension.modules?.speech?.pitch,
          rate: existingExtension.modules?.speech?.rate,
          ssml: existingExtension.modules?.speech?.ssml,
          language: existingExtension.modules?.speech?.language,
        },
        vrm: existingExtension.modules?.vrm,
        live2d: existingExtension.modules?.live2d,
        displayModelId: existingExtension.modules?.displayModelId ?? defaultModules.displayModelId,
        activeBackgroundId: existingExtension.modules?.activeBackgroundId,
        hitInteractions: existingExtension.modules?.hitInteractions,
        artistry: {
          enabled: existingExtension.modules?.artistry?.enabled ?? (existingExtension as any).artistry?.enabled ?? defaultModules.artistry.enabled,
          provider: existingExtension.modules?.artistry?.provider ?? (existingExtension as any).artistry?.provider ?? defaultModules.artistry.provider,
          model: existingExtension.modules?.artistry?.model ?? (existingExtension as any).artistry?.model ?? defaultModules.artistry.model,
          promptPrefix: existingExtension.modules?.artistry?.promptPrefix ?? (existingExtension as any).artistry?.promptPrefix ?? (existingExtension as any).artistry?.prompt_prefix ?? defaultModules.artistry.promptPrefix,
          workflowId: existingExtension.modules?.artistry?.workflowId ?? (existingExtension as any).artistry?.workflowId ?? (existingExtension as any).artistry?.remixId,
          widgetInstruction: existingExtension.modules?.artistry?.widgetInstruction ?? (existingExtension as any).artistry?.widgetInstruction ?? defaultModules.artistry.widgetInstruction,
          spawnMode: existingExtension.modules?.artistry?.spawnMode ?? (existingExtension as any).artistry?.spawnMode ?? defaultModules.artistry.spawnMode,
          options: existingExtension.modules?.artistry?.options ?? (existingExtension as any).artistry?.options ?? defaultModules.artistry.options,
          autonomousEnabled: existingExtension.modules?.artistry?.autonomousEnabled ?? (existingExtension as any).artistry?.autonomousEnabled ?? defaultModules.artistry.autonomousEnabled,
          autonomousThreshold: existingExtension.modules?.artistry?.autonomousThreshold ?? (existingExtension as any).artistry?.autonomousThreshold ?? defaultModules.artistry.autonomousThreshold,
          autonomousTarget: existingExtension.modules?.artistry?.autonomousTarget ?? (existingExtension as any).artistry?.autonomousTarget ?? defaultModules.artistry.autonomousTarget,
        },
      },
      agents: existingExtension.agents ?? {},
      remoteCardId: existingExtension.remoteCardId,
    }
  }

  function newAiriCard(card: Card | ccv3.CharacterCardV3): AiriCard {
    // Callers (addCard/updateCard) guarantee `id` is present on the input.
    const id = (card as unknown as { id: string }).id
    // Handle ccv3 format if needed
    if ('data' in card) {
      const ccv3Card = card as ccv3.CharacterCardV3
      return {
        id,
        name: ccv3Card.data.name,
        version: ccv3Card.data.character_version ?? '1.0.0',
        description: ccv3Card.data.description ?? '',
        creator: ccv3Card.data.creator ?? '',
        notes: ccv3Card.data.creator_notes ?? '',
        notesMultilingual: ccv3Card.data.creator_notes_multilingual,
        personality: ccv3Card.data.personality ?? '',
        scenario: ccv3Card.data.scenario ?? '',
        greetings: [
          ccv3Card.data.first_mes,
          ...(ccv3Card.data.alternate_greetings ?? []),
        ],
        greetingsGroupOnly: ccv3Card.data.group_only_greetings ?? [],
        systemPrompt: ccv3Card.data.system_prompt ?? '',
        postHistoryInstructions: ccv3Card.data.post_history_instructions ?? '',
        messageExample: ccv3Card.data.mes_example
          ? ccv3Card.data.mes_example
              .split('<START>\n')
              .filter(Boolean)
              .map(example => example.split('\n')
                .map((line) => {
                  if (line.startsWith('{{char}}:') || line.startsWith('{{user}}:'))
                    return line as `{{char}}: ${string}` | `{{user}}: ${string}`
                  throw new Error(`Invalid message example format: ${line}`)
                }))
          : [],
        tags: ccv3Card.data.tags ?? [],
        extensions: {
          airi: resolveAiriExtension(ccv3Card),
          ...ccv3Card.data.extensions,
        },
      }
    }

    return {
      ...card,
      id,
      appearance: card.appearance,
      extensions: {
        airi: resolveAiriExtension(card),
        ...card.extensions,
      },
    }
  }

  /**
   * On app boot, re-fetch remote cards from the server to pick up version bumps.
   *
   * Card data (including model URLs with `?v=<version>`) is persisted in
   * localStorage. Without re-fetching, a version change in card.json never
   * reaches the frontend, so the OPFS Live2D cache keeps serving the stale
   * model.zip (the `?v=` query string stays the same).
   *
   * When the server returns a newer version, we update the card's live2d/vrm
   * URL (which carries the new `?v=`) so that:
   *   1. The OPFS cache detects the URL change and re-fetches model.zip.
   *   2. localStorage is updated so subsequent refreshes use the new URL.
   */
  async function initialize() {
    const remoteCards = Array.from(cards.value.values()).filter(
      c => c.extensions?.airi?.remoteCardId,
    )
    if (remoteCards.length === 0)
      return

    await Promise.all(remoteCards.map(async (card) => {
      const remoteId = card.extensions.airi!.remoteCardId!
      try {
        const remote = await fetchCharacterCard(remoteId)
        const remoteVersion = remote.version !== undefined ? String(remote.version) : ''
        if (!remoteVersion || remoteVersion === card.version)
          return

        const newUrl = getDefaultVrmUrl(remote)
        if (!newUrl)
          return

        const newPreviewUrl = getDefaultPreviewUrl(remote)
        const modelType = (remote.modelType || '').toLowerCase()
        const modules = card.extensions.airi!.modules

        // Sync ALL server-side fields that may have changed, while preserving
        // user-configured settings (consciousness, speech, artistry, etc.)
        const updatedCard: AiriCard = {
          ...card,
          name: remote.name,
          description: remote.description,
          version: remoteVersion,
          personality: remote.personality,
          scenario: remote.scenario,
          systemPrompt: remote.systemPrompt,
          greetings: remote.greetings,
          tags: remote.tags,
          appearance: remote.appearance,
          extensions: {
            ...card.extensions,
            airi: {
              ...card.extensions.airi!,
              modules: {
                ...modules,
                live2d: modelType === 'live2d'
                  ? { ...modules.live2d, url: newUrl, previewUrl: newPreviewUrl ?? modules.live2d?.previewUrl }
                  : modules.live2d,
                vrm: modelType === 'vrm'
                  ? { ...modules.vrm, url: newUrl, previewUrl: newPreviewUrl ?? modules.vrm?.previewUrl }
                  : modules.vrm,
                hitInteractions: remote.hitInteractions ?? modules.hitInteractions,
              },
            },
          },
        }
        cards.value.set(card.id, updatedCard)

        // Sync display model URL if the model is already in the store
        const displayModelId = modules.displayModelId
        if (displayModelId) {
          const dm = displayModelsStore.displayModels.find(m => m.id === displayModelId)
          if (dm && dm.type === 'url')
            dm.url = newUrl
        }
      }
      catch {
        // Server unreachable or card deleted — skip silently
      }
    }))
  }

  watchDebounced(activeCard, (newCard: AiriCard | undefined) => {
    artistryStore.resetToGlobal()

    if (!newCard)
      return

    // TODO: Minecraft Agent, etc
    const extension = resolveAiriExtension(newCard)
    if (!extension)
      return

    activeSpeechProvider.value = extension?.modules?.speech?.provider
    activeSpeechModel.value = extension?.modules?.speech?.model
    activeSpeechVoiceId.value = extension?.modules?.speech?.voice_id

    // Apply body model if the card has a display model configured.
    // NOTICE: must set via store property directly (not storeToRefs .value) so Pinia's
    // proxy correctly calls the writable computed setter → stageModelSelectedState → updateStageModel().
    if (extension.modules?.displayModelId) {
      // Skip stage model switch during initial load when display models haven't been
      // loaded from IndexedDB yet. Otherwise updateStageModel() may race with
      // loadDisplayModelsFromIndexedDB(), fail to find the custom model, fall back to
      // preset-live2d-1 (Hiyori), and overwrite stageModelSelectedState — so the later
      // initializeStageModel() call also loads Hiyori instead of the correct character.
      if (!displayModelsStore.displayModelsFromIndexedDBLoading) {
        const modelId = extension.modules.displayModelId
        const modelExists = displayModelsStore.displayModels.some(m => m.id === modelId)
        if (!modelExists) {
          const vrm = extension.modules.vrm
          const live2d = extension.modules.live2d
          const avatarUrl = vrm?.url || live2d?.url
          const avatarFormat = vrm ? DisplayModelFormat.VRM : live2d ? DisplayModelFormat.Live2dZip : undefined
          if (avatarUrl && avatarFormat) {
            displayModelsStore.displayModels.unshift({
              id: modelId,
              format: avatarFormat,
              type: 'url' as const,
              url: avatarUrl,
              name: newCard.name,
              previewImage: vrm?.previewUrl || live2d?.previewUrl,
              importedAt: Date.now(),
            })
          }
        }
        stageModelStore.stageModelSelected = modelId
      }
    }

    if (extension.modules?.artistry) {
      if (extension.modules.artistry.provider)
        artistryStore.activeProvider = extension.modules.artistry.provider
      if (extension.modules.artistry.model)
        artistryStore.activeModel = extension.modules.artistry.model
      if (extension.modules.artistry.promptPrefix)
        artistryStore.defaultPromptPrefix = extension.modules.artistry.promptPrefix
      if (extension.modules.artistry.options)
        artistryStore.providerOptions = extension.modules.artistry.options
    }
  }, { debounce: 300, maxWait: 1000 })

  function resetState() {
    activeCardId.reset()
    cards.reset()
  }

  return {
    cards,
    activeCard,
    activeCardId,
    addCard,
    removeCard,
    updateCard,
    updateActiveCardDisplayModel,
    getCard,
    resetState,
    initialize,

    currentModels: computed(() => {
      return {
        consciousness: {
          provider: activeConsciousnessProvider.value,
          model: activeConsciousnessModel.value,
        },
        speech: {
          provider: activeSpeechProvider.value,
          model: activeSpeechModel.value,
          voice_id: activeSpeechVoiceId.value,
        },
        displayModelId: stageModelStore.stageModelSelected,
        activeBackgroundId: activeCard.value?.extensions?.airi?.modules?.activeBackgroundId,
      } satisfies AiriExtension['modules']
    }),

    systemPrompt: computed(() => {
      const card = activeCard.value
      if (!card)
        return ''

      // Format appearance as compact text
      const appearanceText = card.appearance
        ? Object.entries(card.appearance)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ')
        : ''

      const components = [
        card.systemPrompt,
        appearanceText ? `[Appearance] ${appearanceText}` : '',
        card.personality,
        card.scenario,
        card.extensions?.airi?.modules?.artistry?.widgetInstruction,
      ].filter(Boolean)

      return components.join('\n\n')
    }),
  }
})
